import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  HStack,
  Spinner,
  Text,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  FormControl,
  FormLabel,
  Select,
  Textarea,
  Grid,
  GridItem,
  FormErrorMessage,
  Switch,
  Checkbox,
  Image,
} from "@chakra-ui/react";
import { useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileLines, faSearch, faEdit, faAddressCard, faLocationDot, faImage, faUser } from "@fortawesome/free-solid-svg-icons";
import DataTable from "../AppDataTable";
import apiService from "../../services/ApiService";
import { useAuth } from "../../context/AuthContext";
import * as IconButton from "../../themes/IconButtonSet";
import { tableCustomStyles, StatusActive } from "../../themes/TableStyles";
import { useDropzone } from "react-dropzone";

const FarmerUserManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sessionId } = useAuth();
  const [farmers, setFarmers] = useState([]);
  const [filteredFarmers, setFilteredFarmers] = useState([]);
  const [farmerTypes, setFarmerTypes] = useState([]);
    const [roles, setRoles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterFarmerType, setFilterFarmerType] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    FarmerID: "",
    FirstName: "",
    LastName: "",
    FirstName2: "",
    LastName2: "",
    IDCard: "",
    Gender: "M",
    BirthDate: "",
    AddressNo: "",
    AddressMoo: "",
    AddressRoad: "",
    AddressSubdistrict: "",
    AddressDistrict: "",
    AddressProvince: "",
    AddressZipcode: "",
    FarmerType: "",
    Email: "",
    Phone: "",
    Address: "",
    ProfileImage: "",
    Remark: "",
    Status: "Active",
    Password: "",
    ConfirmPassword: "",
    RoleID: "",
    UserStatus: "1",
  });
  const [isEditing, setIsEditing] = useState(false);
  const [errors, setErrors] = useState({});
  const [privacyChecked, setPrivacyChecked] = useState(false);

  useEffect(() => {
    if (!sessionId) {
      navigate("/login", { state: { from: location.pathname } });
    }
  }, [sessionId, navigate, location.pathname]);

  const columns = [
    {
      name: "รหัสเกษตรกร",
      selector: (row) => row.FarmerID,
      sortable: true,
    },
    {
      name: "ชื่อ-นามสกุล",
      selector: (row) => `${row.FirstName} ${row.LastName}`,
      sortable: true,
    },
    {
      name: "ประเภทเกษตรกร",
      selector: (row) => row.FarmerType || "-",
      sortable: true,
    },
    {
      name: "สถานะ",
      selector: (row) => <StatusActive status={row.Status === "Active"} />,
      sortable: true,
    },
    {
      name: "การดำเนินการ",
      cell: (row) => (
        <IconButton.Edit
          icon={faEdit}
          onClick={() => handleEdit(row)}
          label="แก้ไข"
        />
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "120px",
    },
  ];

  const fetchFarmerTypes = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getFarmerTypeList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setFarmerTypes(response.FarmerTypeList || []);
      }
    } catch (error) {
      console.error("Error fetching farmer types:", error);
    }
  };

  const fetchFarmers = async () => {
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/getFarmerList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        const data = response.farmerList || [];
        setFarmers(data);
        setFilteredFarmers(data);
      }
    } catch (error) {
      console.error("Error fetching farmers:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getRolesList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setRoles(response.RolesList || []);
      }
    } catch (error) {
      console.error("Error fetching roles:", error);
    }
  };

  useEffect(() => {
    if (sessionId) {
      fetchFarmers();
      fetchFarmerTypes();
      fetchRoles();
    }
  }, [sessionId]);

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);
    applyFilters(value, filterFarmerType, filterStatus);
  };

  const handleFarmerTypeFilter = (e) => {
    const value = e.target.value;
    setFilterFarmerType(value);
    applyFilters(searchTerm, value, filterStatus);
  };

  const handleStatusFilter = (e) => {
    const value = e.target.value;
    setFilterStatus(value);
    applyFilters(searchTerm, filterFarmerType, value);
  };

  const applyFilters = (search, farmerType, status) => {
    let filtered = farmers;

    if (search) {
      const searchTerms = search.split(" ").filter((term) => term.length > 0);
      filtered = filtered.filter((row) =>
        searchTerms.every((term) =>
          [
            row.FarmerID.toLowerCase(),
            row.FirstName.toLowerCase(),
            row.LastName.toLowerCase(),
            row.FarmerType.toLowerCase(),
            row.Status.toLowerCase(),
          ].some((field) => field.includes(term))
        )
      );
    }

    if (farmerType) {
      filtered = filtered.filter((row) => row.FarmerType === farmerType);
    }

    if (status) {
      filtered = filtered.filter((row) => row.Status === status);
    }

    setFilteredFarmers(filtered);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setFilterFarmerType("");
    setFilterStatus("");
    setFilteredFarmers(farmers);
  };

  const handleAddNew = () => {
    setFormData({
      FarmerID: "",
      FirstName: "",
      LastName: "",
      FirstName2: "",
      LastName2: "",
      IDCard: "",
      Gender: "M",
      BirthDate: "",
      AddressNo: "",
      AddressMoo: "",
      AddressRoad: "",
      AddressSubdistrict: "",
      AddressDistrict: "",
      AddressProvince: "",
      AddressZipcode: "",
      FarmerType: "",
      Email: "",
      Phone: "",
      Address: "",
      ProfileImage: "",
      Remark: "",
      Status: "Active",
      Password: "",
      ConfirmPassword: "",
      RoleID: "",
      UserStatus: "1",
    });
    setIsEditing(false);
    setShowForm(true);
    setErrors({});
    setPrivacyChecked(false);
  };

  const handleEdit = async (row) => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getFarmerByID",
        data: { FarmerID: row.FarmerID },
        sessionId: sessionId || "",
      });

      if (response.result === "success" && response.farmer) {
        const farmerData = response.farmer;
        const userResponse = await apiService({
          method: "POST",
          url: "/getUserByProfileID",
          data: { ProfileID: row.FarmerID, ProfileType: "Farmer" },
          sessionId: sessionId || "",
        });

        const userData = userResponse.result === "success" && userResponse.user ? userResponse.user : {};

        setFormData({
          ...farmerData,
          Status: farmerData.Status === "Active" ? "Active" : farmerData.Status,
          Password: "",
          ConfirmPassword: "",
          RoleID: userData.RoleID || "",
          UserStatus: userData.Status ? "1" : "0",
        });
        setIsEditing(true);
        setShowForm(true);
        setErrors({});
        setPrivacyChecked(true);
      } else {
        alert("ไม่พบข้อมูลเกษตรกร");
      }
    } catch (error) {
      console.error("Error fetching farmer data:", error);
      alert("เกิดข้อผิดพลาดในการดึงข้อมูลเกษตรกร");
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    validateField(name, value);
  };

  const handleStatusChange = (e) => {
    setFormData({ ...formData, Status: e.target.checked ? "Active" : "Inactive" });
  };

  const handleUserStatusChange = (e) => {
    setFormData({ ...formData, UserStatus: e.target.checked ? "1" : "0" });
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: "image/*",
    onDrop: (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setFormData({ ...formData, ProfileImage: reader.result });
        };
        reader.readAsDataURL(file);
      }
    },
  });

  const validateField = (name, value) => {
    let newErrors = { ...errors };
    switch (name) {
      case "FarmerID":
      case "IDCard":
        if (!value) {
          newErrors[name] = "กรุณากรอกข้อมูล";
        } else if (value.length > 30) {
          newErrors[name] = "ต้องไม่เกิน 30 ตัวอักษร";
        } else {
          delete newErrors[name];
        }
        break;
      case "FirstName":
      case "LastName":
      case "BirthDate":
      case "FarmerType":
      case "Email":
      case "Phone":
        if (!value) {
          newErrors[name] = "กรุณากรอกข้อมูล";
        } else {
          delete newErrors[name];
        }
        break;
      case "Email":
        if (!value) {
          newErrors[name] = "กรุณากรอกอีเมล";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          newErrors[name] = "รูปแบบอีเมลไม่ถูกต้อง";
        } else {
          delete newErrors[name];
        }
        break;
      case "Password":
        if (!isEditing && !value) {
          newErrors[name] = "กรุณากรอกรหัสผ่าน";
        } else if (value && value.length < 6) {
          newErrors[name] = "รหัสผ่านต้องมีอย่างน้อย 6 ตัวอักษร";
        } else {
          delete newErrors[name];
        }
        break;
      case "ConfirmPassword":
        if (!isEditing && !value) {
          newErrors[name] = "กรุณายืนยันรหัสผ่าน";
        } else if (value && value !== formData.Password) {
          newErrors[name] = "รหัสผ่านไม่ตรงกัน";
        } else {
          delete newErrors[name];
        }
        break;
      default:
        delete newErrors[name];
        break;
    }
    setErrors(newErrors);
  };

  const isFormValid = () => {
    return (
      formData.FarmerID &&
      formData.FarmerID.length <= 30 &&
      formData.IDCard &&
      formData.IDCard.length <= 20 &&
      formData.FirstName &&
      formData.LastName &&
      formData.BirthDate &&
      formData.FarmerType &&
      formData.Email &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.Email) &&
      formData.Phone &&
      (!isEditing || (formData.Password ? formData.Password.length >= 6 : true)) &&
      (!isEditing || (formData.Password ? formData.ConfirmPassword === formData.Password : true)) &&
      (!isEditing || (formData.ConfirmPassword ? formData.ConfirmPassword === formData.Password : true)) &&
      Object.keys(errors).length === 0 &&
      privacyChecked
    );
  };

  const handleSave = async () => {
    if (!isFormValid()) {
      alert("กรุณากรอกข้อมูลที่จำเป็นให้ครบถ้วน");
      return;
    }
    setIsLoading(true);
    try {
      const apiUrl = isEditing ? "/updateFarmer" : "/registerFarmer";
      const submitData = {
        ...formData,
        Status: formData.Status === "Active",
        CreateBy: sessionId,
        UpdatedBy: sessionId,
        ProfileImage: formData.ProfileImage || "",
      };

      const response = await apiService({
        method: "POST",
        url: apiUrl,
        data: submitData,
        sessionId: sessionId || "",
      });

      if (response.result === "success") {
        const userData = {
          UserID: formData.Email,
          ProfileID: formData.FarmerID,
          ProfileType: "Farmer",
          FullName: `${formData.FirstName} ${formData.LastName}`,
          RoleID: formData.RoleID || null,
          Status: formData.UserStatus === "1",
          UpdatedBy: sessionId,
        };

        if (formData.Password) {
          userData.Password = formData.Password;
        }

        await apiService({
          method: "POST",
          url: isEditing ? "/updateUserByProfileID" : "/createUser",
          data: userData,
          sessionId: sessionId || "",
        });

        alert(isEditing ? "อัปเดตข้อมูลเกษตรกรสำเร็จ!" : "สร้างข้อมูลเกษตรกรสำเร็จ!");
        setShowForm(false);
        fetchFarmers();
      }
    } catch (error) {
      console.error("Error saving farmer:", error);
      alert("เกิดข้อผิดพลาดในการบันทึก: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setFormData({
      FarmerID: "",
      FirstName: "",
      LastName: "",
      FirstName2: "",
      LastName2: "",
      IDCard: "",
      Gender: "M",
      BirthDate: "",
      AddressNo: "",
      AddressMoo: "",
      AddressRoad: "",
      AddressSubdistrict: "",
      AddressDistrict: "",
      AddressProvince: "",
      AddressZipcode: "",
      FarmerType: "",
      Email: "",
      Phone: "",
      Address: "",
      ProfileImage: "",
      Remark: "",
      Status: "Active",
      Password: "",
      ConfirmPassword: "",
      RoleID: "",
      UserStatus: "1",
    });
    setErrors({});
    setPrivacyChecked(false);
  };

  const handleDelete = async () => {
    if (!window.confirm("คุณแน่ใจหรือไม่ว่าต้องการลบเกษตรกรนี้?")) return;
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/deleteFarmerByID",
        data: {
          ...formData,
          Status: false,
          UpdatedBy: sessionId,
        },
        sessionId: sessionId || "",
      });

      if (response.result === "success") {
        alert("ลบเกษตรกรสำเร็จ!");
        setShowForm(false);
        fetchFarmers();
      }
    } catch (error) {
      console.error("Error deleting farmer:", error);
      alert("เกิดข้อผิดพลาดในการลบ: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (!sessionId) {
    return null;
  }

  return (
    <Box
      p={{ base: 4, md: 10, lg: 35 }}
      m={{ base: 2, md: 10, lg: 25 }}
      borderWidth={1}
      borderRadius="lg"
      boxShadow="lg"
    >
      <VStack spacing={6} align="start">
        <Box
          mb={5}
          borderRadius="full"
          w="100%"
          bgColor="iBlue.600"
          color="white"
          p={2}
        >
          <HStack spacing={5} ml={5}>
            <FontAwesomeIcon icon={faFileLines} color="white" size="xl" />
            <Heading
              as="h1"
              size={{ base: "md", md: "lg" }}
              fontWeight="normal"
            >
              รายการข้อมูลเกษตรกร
            </Heading>
          </HStack>
        </Box>

        {showForm && (
          <Box
            p={5}
            borderWidth={1}
            borderRadius="lg"
            boxShadow="lg"
            w="100%"
            bg="white"
          >
            <Heading size="md" mb={4}>
              {isEditing ? "แก้ไขข้อมูลเกษตรกร" : "เพิ่มข้อมูลเกษตรกร"}
            </Heading>

            <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
              <Box mb={5} borderRadius="full" w="100%" bgColor="iTeal.500" color="white" p={2}>
                <HStack spacing={5} ml={5}>
                  <FontAwesomeIcon icon={faAddressCard} color="white" size="xl" />
                  <Heading size="lg" fontWeight="normal">ข้อมูลทั่วไป</Heading>
                </HStack>
              </Box>
              <Grid templateColumns="repeat(3, 1fr)" gap={6}>
                <GridItem>
                  <FormControl isRequired isInvalid={!!errors.FarmerID}>
                    <FormLabel>รหัสเกษตรกร</FormLabel>
                    <Input
                      name="FarmerID"
                      value={formData.FarmerID}
                      onChange={handleInputChange}
                      isDisabled={isEditing}
                      colorScheme="iTeal"
                    />
                    <FormErrorMessage>{errors.FarmerID}</FormErrorMessage>
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl isRequired isInvalid={!!errors.IDCard}>
                    <FormLabel>เลขบัตรประจำตัวประชาชน</FormLabel>
                    <Input
                      name="IDCard"
                      value={formData.IDCard}
                      onChange={handleInputChange}
                      isDisabled={isEditing}
                      colorScheme="iTeal"
                    />
                    <FormErrorMessage>{errors.IDCard}</FormErrorMessage>
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl isRequired isInvalid={!!errors.FirstName}>
                    <FormLabel>ชื่อ</FormLabel>
                    <Input
                      name="FirstName"
                      value={formData.FirstName}
                      onChange={handleInputChange}
                      colorScheme="iTeal"
                    />
                    <FormErrorMessage>{errors.FirstName}</FormErrorMessage>
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl isRequired isInvalid={!!errors.LastName}>
                    <FormLabel>นามสกุล</FormLabel>
                    <Input
                      name="LastName"
                      value={formData.LastName}
                      onChange={handleInputChange}
                      colorScheme="iTeal"
                    />
                    <FormErrorMessage>{errors.LastName}</FormErrorMessage>
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl>
                    <FormLabel>ชื่อ (ภาษาอื่น)</FormLabel>
                    <Input
                      name="FirstName2"
                      value={formData.FirstName2}
                      onChange={handleInputChange}
                      colorScheme="iTeal"
                    />
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl>
                    <FormLabel>นามสกุล (ภาษาอื่น)</FormLabel>
                    <Input
                      name="LastName2"
                      value={formData.LastName2}
                      onChange={handleInputChange}
                      colorScheme="iTeal"
                    />
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl isRequired>
                    <FormLabel>เพศ</FormLabel>
                    <Select
                      name="Gender"
                      value={formData.Gender}
                      onChange={handleInputChange}
                      colorScheme="iTeal"
                    >
                      <option value="M">ชาย</option>
                      <option value="F">หญิง</option>
                    </Select>
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl isRequired isInvalid={!!errors.BirthDate}>
                    <FormLabel>วันเกิด</FormLabel>
                    <Input
                      name="BirthDate"
                      type="date"
                      value={formData.BirthDate}
                      onChange={handleInputChange}
                      colorScheme="iTeal"
                    />
                    <FormErrorMessage>{errors.BirthDate}</FormErrorMessage>
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl isRequired isInvalid={!!errors.FarmerType}>
                    <FormLabel>ประเภทเกษตรกร</FormLabel>
                    <Select
                      name="FarmerType"
                      value={formData.FarmerType}
                      onChange={handleInputChange}
                      placeholder="เลือกประเภทเกษตรกร"
                      colorScheme="iTeal"
                    >
                      {farmerTypes.map((type) => (
                        <option key={type.FarmerTypeID} value={type.FarmerTypeID}>
                          {type.FarmerTypeName}
                        </option>
                      ))}
                    </Select>
                    <FormErrorMessage>{errors.FarmerType}</FormErrorMessage>
                  </FormControl>
                </GridItem>
              </Grid>
            </Box>

            <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
              <Box mb={5} borderRadius="full" w="100%" bgColor="iBlue.500" color="white" p={2}>
                <HStack spacing={5} ml={5}>
                  <FontAwesomeIcon icon={faLocationDot} color="white" size="xl" />
                  <Heading size="lg" fontWeight="normal">ข้อมูลที่อยู่</Heading>
                </HStack>
              </Box>
              <Grid templateColumns="repeat(3, 1fr)" gap={6}>
                <GridItem>
                  <FormControl>
                    <FormLabel>บ้านเลขที่</FormLabel>
                    <Input
                      name="AddressNo"
                      value={formData.AddressNo}
                      onChange={handleInputChange}
                      colorScheme="iBlue"
                    />
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl>
                    <FormLabel>หมู่</FormLabel>
                    <Input
                      name="AddressMoo"
                      value={formData.AddressMoo}
                      onChange={handleInputChange}
                      colorScheme="iBlue"
                    />
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl>
                    <FormLabel>ถนน</FormLabel>
                    <Input
                      name="AddressRoad"
                      value={formData.AddressRoad}
                      onChange={handleInputChange}
                      colorScheme="iBlue"
                    />
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl>
                    <FormLabel>ตำบล</FormLabel>
                    <Input
                      name="AddressSubdistrict"
                      value={formData.AddressSubdistrict}
                      onChange={handleInputChange}
                      colorScheme="iBlue"
                    />
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl>
                    <FormLabel>อำเภอ</FormLabel>
                    <Input
                      name="AddressDistrict"
                      value={formData.AddressDistrict}
                      onChange={handleInputChange}
                      colorScheme="iBlue"
                    />
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl>
                    <FormLabel>จังหวัด</FormLabel>
                    <Input
                      name="AddressProvince"
                      value={formData.AddressProvince}
                      onChange={handleInputChange}
                      colorScheme="iBlue"
                    />
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl>
                    <FormLabel>รหัสไปรษณีย์</FormLabel>
                    <Input
                      name="AddressZipcode"
                      value={formData.AddressZipcode}
                      onChange={handleInputChange}
                      colorScheme="iBlue"
                    />
                  </FormControl>
                </GridItem>
                <GridItem colSpan={3}>
                  <FormControl>
                    <FormLabel>ที่อยู่</FormLabel>
                    <Input
                      name="Address"
                      value={formData.Address}
                      onChange={handleInputChange}
                      colorScheme="iBlue"
                    />
                  </FormControl>
                </GridItem>
                <GridItem colSpan={3}>
                  <FormControl>
                    <FormLabel>หมายเหตุ</FormLabel>
                    <Textarea
                      name="Remark"
                      value={formData.Remark}
                      onChange={handleInputChange}
                      colorScheme="iBlue"
                    />
                  </FormControl>
                </GridItem>
              </Grid>
            </Box>

            <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
              <Box mb={5} borderRadius="full" w="100%" bgColor="iBlue.500" color="white" p={2}>
                <HStack spacing={5} ml={5}>
                  <FontAwesomeIcon icon={faLocationDot} color="white" size="xl" />
                  <Heading size="lg" fontWeight="normal">ข้อมูลการติดต่อ</Heading>
                </HStack>
              </Box>
              <Grid templateColumns="repeat(3, 1fr)" gap={6}>
                <GridItem>
                  <FormControl isRequired isInvalid={!!errors.Email}>
                    <FormLabel>อีเมล</FormLabel>
                    <Input
                      name="Email"
                      value={formData.Email}
                      onChange={handleInputChange}
                      colorScheme="iBlue"
                    />
                    <FormErrorMessage>{errors.Email}</FormErrorMessage>
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl isRequired isInvalid={!!errors.Phone}>
                    <FormLabel>เบอร์โทรศัพท์</FormLabel>
                    <Input
                      name="Phone"
                      value={formData.Phone}
                      onChange={handleInputChange}
                      colorScheme="iBlue"
                    />
                    <FormErrorMessage>{errors.Phone}</FormErrorMessage>
                  </FormControl>
                </GridItem>
              </Grid>
            </Box>

            <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
              <Box mb={5} borderRadius="full" w="100%" bgColor="iGreen.500" color="white" p={2}>
                <HStack spacing={5} ml={5}>
                  <FontAwesomeIcon icon={faImage} color="white" size="xl" />
                  <Heading size="lg" fontWeight="normal">รูปโปรไฟล์</Heading>
                </HStack>
              </Box>
              <Box {...getRootProps()} borderWidth={2} borderStyle="dashed" borderRadius="md" w="70%" p={5} textAlign="center" cursor="pointer">
                <Input {...getInputProps()} colorScheme="iGreen" />
                {formData.ProfileImage ? (
                  <Image src={formData.ProfileImage} alt="Profile Preview" maxH="200px" mx="auto" />
                ) : (
                  <Text h={20}>ลากและวางรูปภาพที่นี่ หรือคลิกเพื่อเลือกไฟล์</Text>
                )}
              </Box>
            </Box>

            <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
              <Box mb={5} borderRadius="full" w="100%" bgColor="iPurple.500" color="white" p={2}>
                <HStack spacing={5} ml={5}>
                  <FontAwesomeIcon icon={faUser} color="white" size="xl" />
                  <Heading size="lg" fontWeight="normal">ชื่อผู้ใช้และรหัสผ่าน</Heading>
                </HStack>
              </Box>
              <Grid templateColumns="repeat(3, 1fr)" gap={6}>
                <GridItem>
                  <FormControl isRequired isInvalid={!!errors.Email}>
                    <FormLabel>ชื่อผู้ใช้ (อีเมล)</FormLabel>
                    <Input
                      name="Email"
                      value={formData.Email}
                      onChange={handleInputChange}
                      isDisabled
                      colorScheme="iPurple"
                    />
                    <FormErrorMessage>{errors.Email}</FormErrorMessage>
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl isRequired={!isEditing} isInvalid={!!errors.Password}>
                    <FormLabel>รหัสผ่าน</FormLabel>
                    <Input
                      name="Password"
                      type="password"
                      value={formData.Password}
                      onChange={handleInputChange}
                      colorScheme="iPurple"
                    />
                    <FormErrorMessage>{errors.Password}</FormErrorMessage>
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl isRequired={!isEditing} isInvalid={!!errors.ConfirmPassword}>
                    <FormLabel>ยืนยันรหัสผ่าน</FormLabel>
                    <Input
                      name="ConfirmPassword"
                      type="password"
                      value={formData.ConfirmPassword}
                      onChange={handleInputChange}
                      colorScheme="iPurple"
                    />
                    <FormErrorMessage>{errors.ConfirmPassword}</FormErrorMessage>
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl>
                    <FormLabel>สิทธิ์การใช้งาน</FormLabel>
                    <Select
                      name="RoleID"
                      value={formData.RoleID}
                      onChange={handleInputChange}
                      placeholder="เลือกสิทธิ์การใช้งาน"
                      colorScheme="iPurple"
                    >
                      {roles.map((role) => (
                        <option key={role.RoleID} value={role.RoleID}>
                          {role.RoleName}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl>
                    <FormLabel>สถานะผู้ใช้</FormLabel>
                    <Switch
                      name="UserStatus"
                      isChecked={formData.UserStatus === "1"}
                      onChange={handleUserStatusChange}
                      colorScheme="iPurple"
                    />
                  </FormControl>
                </GridItem>
              </Grid>
            </Box>

            <Box p={5}>
              <Checkbox
                isChecked={privacyChecked}
                onChange={(e) => setPrivacyChecked(e.target.checked)}
                size="lg"
                colorScheme="iOrange"
              >
                ฉันได้อ่านนโยบายความเป็นส่วนตัวและเข้าใจนโยบายความเป็นส่วนตัวของเราแล้ว
                และยอมรับเงื่อนไขการใช้งาน รวมถึงนโยบายความเป็นส่วนตัวของเรา
                และยอมรับการใช้งานของคุกกี้ และเทคโนโลยีต่างๆ ที่เกี่ยวข้อง
              </Checkbox>
            </Box>

            <HStack mt={6} justify="flex-end">
              <IconButton.Close onClick={handleCancel} label="ปิด" />
              {isEditing && (
                <IconButton.Delete onClick={handleDelete} label="ลบ" />
              )}
              <IconButton.Save onClick={handleSave} isLoading={isLoading} />
            </HStack>
          </Box>
        )}

        <HStack
          w="100%"
          justify="space-between"
          flexWrap={{ base: "wrap", md: "nowrap" }}
          spacing={4}
        >
          <HStack spacing={4} flexWrap={{ base: "wrap", md: "nowrap" }}>
            <InputGroup w={{ base: "100%", md: "300px" }}>
              <InputLeftElement pointerEvents="none">
                <FontAwesomeIcon icon={faSearch} color="gray.300" />
              </InputLeftElement>
              <Input
                placeholder="ค้นหาเกษตรกร..."
                value={searchTerm}
                onChange={handleSearch}
                colorScheme="iBlue"
              />
            </InputGroup>
            <Select
              w={{ base: "100%", md: "200px" }}
              value={filterFarmerType}
              onChange={handleFarmerTypeFilter}
              placeholder="เลือกประเภทเกษตรกร"
              colorScheme="iBlue"
            >
              <option value="">ทั้งหมด</option>
              {farmerTypes.map((type) => (
                <option key={type.FarmerTypeID} value={type.FarmerTypeID}>
                  {type.FarmerTypeName}
                </option>
              ))}
            </Select>
            <Select
              w={{ base: "100%", md: "200px" }}
              value={filterStatus}
              onChange={handleStatusFilter}
              placeholder="เลือกสถานะ"
              colorScheme="iBlue"
            >
              <option value="">ทั้งหมด</option>
              <option value="Active">Active</option>
              <option value="Retired">Retired</option>
              <option value="Resigned">Resigned</option>
            </Select>
            <IconButton.Clear onClick={handleClearSearch} label="ล้าง" />
          </HStack>
          {/*<IconButton.Add onClick={handleAddNew} label="เพิ่มข้อมูลใหม่" />*/}
        </HStack>

        {isLoading ? (
          <Spinner size="md" />
        ) : farmers.length === 0 ? (
          <Text fontSize="lg" color="gray.500" textAlign="center" w="100%">
            ยังไม่มีข้อมูลเกษตรกร
          </Text>
        ) : filteredFarmers.length === 0 ? (
          <Text fontSize="lg" color="gray.500" textAlign="center" w="100%">
            ไม่พบข้อมูลที่ค้นหา
          </Text>
        ) : (
          <Box w="100%" overflowX="auto">
            <DataTable
              columns={columns}
              data={filteredFarmers}
              pagination
              highlightOnHover
              striped
              responsive
              customStyles={tableCustomStyles}
            />
          </Box>
        )}
      </VStack>
    </Box>
  );
};

export default FarmerUserManagement;